import React, { useEffect, useMemo, useState } from "react";
import {
  FaEye,
  FaTrash,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
} from "react-icons/fa";
import api from "../../api";

const PAGE_SIZE = 10;

const ApplicantView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const [q, setQ] = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [filterPayment, setFilterPayment] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const schoolId = localStorage.getItem("school_id");
  const baseURL = "/application";

  useEffect(() => {
    fetchApplicants();
  }, []);

  const fetchApplicants = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${baseURL}/list?school_id=${schoolId}`);
      if (res.data?.status && Array.isArray(res.data.data)) {
        setItems(
          res.data.data
            .slice()
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        );
      } else {
        setItems([]);
        setMsg({ type: "info", text: "No applicants found." });
      }
    } catch (err) {
      setMsg({ type: "error", text: "Network error while fetching applicants." });
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let data = items.slice();
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      data = data.filter(
        (a) =>
          (a.name || "").toLowerCase().includes(s) ||
          (a.application_no || "").toLowerCase().includes(s) ||
          (a.mobile_no || "").toLowerCase().includes(s)
      );
    }
    if (filterClass) data = data.filter((a) => a.class_applied === filterClass);
    if (filterPayment) data = data.filter((a) => a.payment_status === filterPayment);
    return data;
  }, [items, q, filterClass, filterPayment]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this applicant?")) return;
    try {
      const res = await api.post(`${baseURL}/delete`, { id });
      if (res.data?.status) {
        setMsg({ type: "success", text: res.data.message || "Deleted successfully" });
        fetchApplicants();
      } else {
        setMsg({ type: "error", text: res.data.message || "Delete failed" });
      }
    } catch (err) {
      setMsg({ type: "error", text: "Network error" });
    }
  };

  const classesList = useMemo(() => {
    const s = new Set();
    items.forEach((i) => i.class_applied && s.add(i.class_applied));
    return Array.from(s).sort();
  }, [items]);

  const badgeStyle = (status) => {
    const v = (status || "").toLowerCase();
    if (v === "paid") return { bg: "#dcfce7", color: "#166534", label: "Paid" };
    if (v === "pending") return { bg: "#fef9c3", color: "#92400e", label: "Pending" };
    return { bg: "#f3f4f6", color: "#374151", label: "—" };
  };

  return (
    <div style={pageRoot}>
      <div style={outerCard}>
        <div style={header}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={headerIcon}>📋</div>
            <div>
              <div style={headerTitle}>Pre-Admission Applicants</div>
              <div style={headerSub}>Review and manage all applications</div>
            </div>
          </div>
          <div style={searchWrap}>
            <FaSearch style={{ marginLeft: 8, color: "#6b7280" }} />
            <input
              placeholder="Search by name / app no / mobile..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              style={searchInput}
            />
          </div>
        </div>

        {/* Filters */}
        <div style={filters}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select
              value={filterClass}
              onChange={(e) => {
                setFilterClass(e.target.value);
                setPage(1);
              }}
              style={selectStyle}
            >
              <option value="">All Classes</option>
              {classesList.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              value={filterPayment}
              onChange={(e) => {
                setFilterPayment(e.target.value);
                setPage(1);
              }}
              style={selectStyle}
            >
              <option value="">All Payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <button
            onClick={() => {
              setFilterClass("");
              setFilterPayment("");
              setQ("");
              setPage(1);
            }}
            style={btnReset}
          >
            Reset
          </button>
        </div>

        {/* Message */}
        {msg && (
          <div
            style={{
              marginBottom: 10,
              color: msg.type === "error" ? "#b91c1c" : "#047857",
            }}
          >
            {msg.text}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={loadingBox}>Loading applicants...</div>
        ) : filtered.length === 0 ? (
          <div style={emptyBox}>No applicants found.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={table}>
              <thead style={thead}>
                <tr>
                  <th>Photo</th>
                  <th>Application No</th>
                  <th>Name</th>
                  <th>Father Name</th>
                  <th>Mobile</th>
                  <th>Class Applied</th>
                  <th>Previous Class</th>
                  <th>Total Marks</th>
                  <th>Mark Obtained</th>
                  <th>%</th>
                  <th>Payment</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((a) => {
                  const pay = badgeStyle(a.payment_status);
                  return (
                    <tr key={a.id}>
                      <td>
                        {a.photo ? (
                          <img
                            src={`https://dgps.co.in/${a.photo}`}
                            alt={a.name}
                            style={{
                              width: 50,
                              height: 50,
                              borderRadius: 10,
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div style={noPhotoBox}>No Photo</div>
                        )}
                      </td>
                      <td>{a.application_no}</td>
                      <td>{a.name}</td>
                      <td>{a.father_name}</td>
                      <td>{a.mobile_no}</td>
                      <td>{a.class_applied}</td>
                      <td>{a.previous_class}</td>
                      <td>{a.total_marks}</td>
                      <td>{a.previous_marks}</td>
                      <td>{a.previous_percent}</td>
                      <td>
                        <span
                          style={{
                            background: pay.bg,
                            color: pay.color,
                            fontWeight: 600,
                            fontSize: 13,
                            padding: "4px 10px",
                            borderRadius: 999,
                          }}
                        >
                          {pay.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            title="View Full Details"
                            onClick={() => setSelected(a)}
                            style={actionBtnView}
                          >
                            <FaEye />
                          </button>
                          <a
                            href={a.qr_code_url}
                            target="_blank"
                            rel="noreferrer"
                            title="View Application Copy"
                            style={actionBtnLink}
                          >
                            <FaExternalLinkAlt />
                          </a>
                          <button
                            title="Delete"
                            onClick={() => handleDelete(a.id)}
                            style={actionBtnDel}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filtered.length > 0 && (
          <div style={pager}>
            <div style={{ color: "#374151" }}>
              Page {page} of {totalPages}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} style={pagerBtn}>
                <FaChevronLeft />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={pagerBtn}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
                {/* Modal */}
        {selected && (
          <div style={modalOverlay}>
            <div style={modalCard}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 14,
                }}
              >
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <img
                    src={`https://dgps.co.in/${selected.photo}`}
                    alt={selected.name}
                    style={{
                      width: 76,
                      height: 76,
                      borderRadius: 10,
                      objectFit: "cover",
                      border: "1px solid #e6e6e6",
                    }}
                  />
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{selected.name}</div>
                    <div style={{ color: "#6b7280", fontSize: 13 }}>
                      {selected.application_no} • Applied for {selected.class_applied}
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} style={btnOutline}>
                  Close
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div style={detailBox}>
                  <DetailRow label="DOB" value={selected.dob} />
                  <DetailRow label="Gender" value={selected.gender} />
                  <DetailRow label="Father" value={selected.father_name} />
                  <DetailRow label="Mother" value={selected.mother_name} />
                  <DetailRow label="Mobile" value={selected.mobile_no} />
                  <DetailRow label="PIN" value={selected.pin} />
                </div>

                <div style={detailBox}>
                  <DetailRow label="Prev Class" value={selected.previous_class} />
                  <DetailRow label="Prev School" value={selected.previous_school_name} />
                  <DetailRow label="Marks" value={selected.previous_marks} />
                  <DetailRow label="%" value={selected.previous_percent} />
                  <DetailRow label="Payment" value={selected.payment_status} />
                  <DetailRow label="Method" value={selected.payment_method || "-"} />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
                    Documents
                  </div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {renderDoc("Photo", selected.photo)}
                    {renderDoc("Aadhaar", selected.aadhaar_card)}
                    {renderDoc("Birth Cert", selected.birth_certificate)}
                    {renderDoc("Marksheet", selected.marksheet)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
const DetailRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      borderBottom: "1px dashed #e5e7eb",
      fontSize: 14,
    }}
  >
    <div style={{ color: "#6b7280" }}>{label}</div>
    <div style={{ color: "#111", fontWeight: 600 }}>{value || "-"}</div>
  </div>
);

const renderDoc = (label, path) => {
  if (!path) {
    return (
      <div key={label} style={docEmpty}>
        <div>{label}</div>
        <div style={{ fontSize: 12, color: "#9ca3af" }}>— not uploaded —</div>
      </div>
    );
  }
  const url = path.startsWith("http") ? path : `https://dgps.co.in/${path}`;
  return (
    <a key={label} href={url} target="_blank" rel="noreferrer" style={docItem}>
      <img
        src={url}
        alt={label}
        style={{
          width: 110,
          height: 70,
          objectFit: "cover",
          borderRadius: 6,
          border: "1px solid #e6e6e6",
        }}
      />
      <div style={{ fontSize: 12, marginTop: 4 }}>{label}</div>
    </a>
  );
};

/* ---------- styles ---------- */
const pageRoot = {
  background: "#f1f5f9",
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: "40px 20px",
};
const outerCard = {
  background: "#fff",
  width: "100%",
  maxWidth: "1280px",
  borderRadius: 16,
  boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
  padding: "25px 30px",
};
const header = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 };
const headerIcon = { width: 52, height: 52, borderRadius: 12, background: "linear-gradient(135deg,#c7d2fe,#e0f2fe)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 };
const headerTitle = { fontSize: 22, fontWeight: 800, color: "#0f172a" };
const headerSub = { fontSize: 13, color: "#6b7280" };
const searchWrap = { display: "flex", alignItems: "center", background: "#f9fafb", borderRadius: 10, padding: "6px 10px", width: 400, border: "1px solid #e2e8f0" };
const searchInput = { border: "none", outline: "none", padding: "8px 10px", fontSize: 14, flex: 1, background: "transparent" };
const filters = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 8 };
const selectStyle = { padding: "8px 12px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", fontSize: 14 };
const btnReset = { background: "#fff", color: "#0f172a", border: "1px solid #e2e8f0", padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontWeight: 600 };
const table = { width: "100%", borderCollapse: "collapse", background: "#fff" };
const thead = { background: "#f8fafc", color: "#0f172a" };
const loadingBox = { padding: 30, textAlign: "center", color: "#6b7280" };
const emptyBox = { padding: 30, textAlign: "center", color: "#6b7280" };
const pager = { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20 };
const pagerBtn = { background: "#fff", border: "1px solid #e2e8f0", padding: "8px 10px", borderRadius: 10, cursor: "pointer" };
const actionBtnView = { background: "#e0f2fe", color: "#0369a1", border: "none", borderRadius: 8, padding: 8, cursor: "pointer", transition: "0.2s" };
const actionBtnLink = { background: "#dcfce7", color: "#166534", border: "none", borderRadius: 8, padding: 8, cursor: "pointer", transition: "0.2s" };
const actionBtnDel = { background: "#fee2e2", color: "#b91c1c", border: "none", borderRadius: 8, padding: 8, cursor: "pointer", transition: "0.2s" };
const noPhotoBox = { width: 50, height: 50, borderRadius: 10, marginLeft: "10px", border: "2px dashed #d1d5db", color: "#9ca3af", fontSize: 10, display: "flex", background: "#f9fafb" };
const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999, padding: 20 };
const modalCard = { background: "#fff", padding: 20, borderRadius: 12, width: "100%", maxWidth: 920, maxHeight: "92vh", overflowY: "auto" };
const detailBox = { background: "#f9fafb", borderRadius: 10, padding: 12 };
const docItem = { display: "flex", flexDirection: "column", gap: 4, alignItems: "center", textDecoration: "none", color: "#111" };
const docEmpty = { width: 120, padding: 10, borderRadius: 8, border: "1px dashed #d1d5db", background: "#fff", textAlign: "center" };
const btnOutline = { background: "#fff", color: "#0f172a", border: "1px solid #e6e6e6", padding: "8px 12px", borderRadius: 8, cursor: "pointer" };
export default ApplicantView;
